import React from 'react';
import { View, Text, Image, StyleSheet, ScrollView } from 'react-native';
import ReactDOM from 'react-dom';
import { MdChevronLeft, MdChevronRight, MdClose } from 'react-icons/md';
import CardRenderer from './CardRenderer';
import { getAvatarSource } from '@cubemeet/core/utils/avatarImageMapper';
import { getPresetAvatarAsset } from '@cubemeet/ui';

interface SnapshotImage {
  url: string;
  alt?: string;
  title?: string;
  key?: string;
}

interface SnapshotNote {
  id: string;
  text: string;
  images?: SnapshotImage[];
  author?: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  createdAt?: string;
}

interface SnapshotEntry {
  id: string;
  title: string;
  summary?: string;
  coverImage?: SnapshotImage;
  gallery?: SnapshotImage[];
  notes?: SnapshotNote[];
  meta?: Record<string, any>;
  order?: number;
}

interface SnapshotDay {
  id: string;
  date?: string;
  dayNumber?: number;
  entries: SnapshotEntry[];
}

interface SnapshotContent {
  title?: string;
  city?: string;
  coverImage?: string;
  days: SnapshotDay[];
  meta?: {
    dayCount?: number;
    entryCount?: number;
    mediaCount?: number;
    sourceItineraryId?: string;
  };
  schemaVersion?: string;
  generatedAt?: string;
}

interface SnapshotContentRendererProps {
  contentJson: SnapshotContent | string;
  authorId?: string; // 游记作者 ID，用于过滤图片
  t?: (key: string, options?: any) => string;
  locale?: string;
  /** 當用戶點擊「加入行程」按鈕時的回調，傳入 entry 的 payload 數據 */
  onAddToItinerary?: (entryPayload: {
    title: string;
    url?: string;
    imageUrl?: string;
    description?: string;
    payload?: any;
  }) => void;
  /** Optional callback to handle lightbox opening externally. If provided, internal lightbox logic is skipped. */
  onOpenLightbox?: (images: SnapshotImage[], startIndex: number) => void;
  /** Callback when a hashtag is pressed */
  onTagPress?: (tag: string) => void;
}

  // 解析图片 URL，处理相对路径
const resolveImageSource = (url: string | undefined) => {
  if (!url) return null;
  
  // 如果是完整的 HTTP(S) URL，直接返回
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return { uri: url };
  }
  
  // 如果是相对路径，转换为绝对路径
  if (url.startsWith('/')) {
    // 使用当前域名
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    return { uri: `${baseUrl}${url}` };
  }
  
  // 其他情况直接尝试使用
  return { uri: url };
};

interface SnapshotEntryItemProps {
  entry: SnapshotEntry;
  entryIndex: number;
  authorId?: string;
  t: (key: string, options?: any) => string;
  openLightbox: (images: SnapshotImage[], startIndex: number) => void;
  onTagPress?: (tag: string) => void;
  onAddToItinerary?: (entryPayload: {
    title: string;
    url?: string;
    imageUrl?: string;
    description?: string;
    payload?: any;
  }) => void;
}

const SnapshotEntryItem: React.FC<SnapshotEntryItemProps> = ({ entry, entryIndex, authorId, t: _t, openLightbox, onAddToItinerary, onTagPress }) => {
  // 过滤图片：
  // 1. 排除用户上传的图片（有 uploaded_by 字段的），这些图片属于 notes，会在 notes 区域显示
  // 2. 只保留公共图片（如 cover_image、base_payload.imageUrl 等）
  const filteredGallery = React.useMemo(() => {
    if (!entry.gallery || entry.gallery.length === 0) return [];
    
    // 只保留没有 uploaded_by 的图片（公共图片如 cover_image）
    // 用户上传的图片（有 uploaded_by）属于 notes，不应该在卡片封面显示
    const filtered = entry.gallery.filter((img: any) => !img.uploaded_by);
    
    return filtered;
  }, [entry.gallery]);
  
  const hasGallery = filteredGallery && filteredGallery.length > 0;
  const hasNotes = entry.notes && entry.notes.length > 0;
  const scrollRef = React.useRef<ScrollView>(null);
  const [scrollPosition, setScrollPosition] = React.useState(0);
  const [galleryWidth, setGalleryWidth] = React.useState(0);
  const [contentWidth, setContentWidth] = React.useState(0);
  const [showNav, setShowNav] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  // 檢測是否為交通類型或外部分享類型（視頻等）
  const itemType = entry.meta?.itemType?.toLowerCase() || '';
  const isTransport = itemType === 'transport' || itemType === 'flight' || itemType === 'train' || itemType === 'bus';
  const isExternal = itemType === 'external';

  const isSingleImage = hasGallery && filteredGallery!.length === 1;
  const isMultipleImages = hasGallery && filteredGallery!.length > 1;

  const parseTextWithTags = (text: string) => {
    if (!text || !text.includes('#')) return text;
    
    // Split by hashtag pattern, capturing the hashtag
    const parts = text.split(/(#[\w\u4e00-\u9fa5]+)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('#') && part.length > 1) {
        const tag = part.substring(1); // remove #
        return (
          <Text 
            key={index} 
            style={{ color: '#0ea5a4', fontWeight: '500' }}
            onPress={() => onTagPress?.(tag)}
          >
            {part}
          </Text>
        );
      }
      return <Text key={index}>{part}</Text>;
    });
  };

  const renderNote = (note: SnapshotNote, noteIdx: number) => {
    const avatarSource = note.author?.avatarUrl 
      ? (getAvatarSource(note.author.avatarUrl) || getPresetAvatarAsset(note.author.avatarUrl)) 
      : null;
    return (
      <View key={note.id || noteIdx} style={styles.noteContainer}>
        <View style={styles.noteHeader}>
          {avatarSource && (
            <Image source={avatarSource} style={styles.noteAuthorAvatar} />
          )}
          <Text style={styles.noteAuthorName}>{note.author?.name || ''}</Text>
        </View>

        {note.text ? <Text style={styles.noteText}>{parseTextWithTags(note.text)}</Text> : null}

        {note.images && note.images.length > 0 && (
          <View style={styles.noteImagesContainer}>
            {note.images.map((img, idx) => {
              const imgSource = resolveImageSource(img.url);
              if (!imgSource) return null;
              return (
                <div
                  key={img.key || img.url || idx}
                  onClick={(e) => { e.stopPropagation(); openLightbox(note.images!, idx); }}
                  style={{ cursor: 'pointer' }}
                >
                  <Image
                    source={imgSource}
                    style={styles.noteImage}
                    resizeMode="cover"
                  />
                </div>
              );
            })}
          </View>
        )}
      </View>
    );
  };

  const handleScroll = (event: any) => {
    setScrollPosition(event.nativeEvent.contentOffset.x);
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      const newPosition = Math.max(0, scrollPosition - 300);
      (scrollRef.current as any).scrollTo({ x: newPosition, animated: true });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      const maxScroll = Math.max(0, contentWidth - galleryWidth);
      const newPosition = Math.min(maxScroll, scrollPosition + 300);
      (scrollRef.current as any).scrollTo({ x: newPosition, animated: true });
    }
  };

  const canScrollLeft = scrollPosition > 10;
  const canScrollRight = scrollPosition < (contentWidth - galleryWidth - 10);

  // 如果是交通類型或外部分享類型（視頻等），使用 CardRenderer 渲染
  if (isTransport || isExternal) {
    // 重構成 CardRenderer 期望的格式，保留完整的 payload 和 card 數據
    const cardItem = {
      id: entry.id,
      title: entry.title,
      type: entry.meta?.itemType || (isTransport ? 'transport' : 'external'),
      time: entry.meta?.start_time,
      start_time: entry.meta?.start_time,
      end_time: entry.meta?.end_time,
      // note: entry.summary, // 不要使用 summary 作為 note，因為 summary 可能是 AI 生成的結構化摘要，而我們希望顯示卡片的原始描述
      meta: entry.meta,
      payload: entry.payload,
      custom_payload: entry.payload,
      card: entry.card,
      card_id: entry.meta?.card_id,
    };
    
    // 對於 external（視頻），使用 div 包裝以避免 View 捕獲觸摸事件
    if (isExternal) {
      // 獲取外部鏈接信息
      const externalUrl = 
        entry.card?.base_payload?.url || 
        entry.payload?.url || 
        entry.payload?.website ||
        entry.payload?.resolvedFinalUrl ||
        entry.card?.website ||
        entry.meta?.externalUrl ||
        cardItem.payload?.url ||
        cardItem.payload?.website ||
        cardItem.card?.base_payload?.url;
      
      const siteName = 
        entry.card?.base_payload?.siteName || 
        entry.payload?.siteName || 
        entry.card?.base_payload?.site_name ||
        entry.payload?.site_name ||
        cardItem.payload?.siteName ||
        cardItem.card?.base_payload?.siteName ||
        '';
      
      const partner = 
        entry.card?.base_payload?.partner || 
        entry.payload?.partner ||
        cardItem.payload?.partner ||
        cardItem.card?.base_payload?.partner ||
        '';
      
      const favicon = 
        entry.card?.base_payload?.favicon || 
        entry.payload?.favicon ||
        cardItem.payload?.favicon ||
        cardItem.card?.base_payload?.favicon;
      
      const getButtonText = () => {
        const p = (partner || '').toLowerCase();
        const s = (siteName || '').toLowerCase();
        if (p.includes('trip') || s.includes('trip')) return '在 Trip.com 查看';
        if (p.includes('klook') || s.includes('klook')) return '在 Klook 查看';
        if (p.includes('kkday') || s.includes('kkday')) return '在 KKday 查看';
        if (p.includes('booking') || s.includes('booking')) return '在 Booking.com 查看';
        if (p.includes('agoda') || s.includes('agoda')) return '在 Agoda 查看';
        if (siteName) return `在 ${siteName} 查看`;
        return '查看詳情';
      };
      
      return (
        <div key={entry.id} style={{
          marginBottom: 24,
          backgroundColor: '#fff',
          borderRadius: 12,
          border: '1px solid #E5E7EB',
          position: 'relative',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <View style={{ padding: 16, paddingBottom: 0 }}>
            <View style={styles.entryHeader}>
              <View style={styles.entryNumberBadge}>
                <Text style={styles.entryNumberText}>{entryIndex + 1}</Text>
              </View>
              <Text style={styles.entryTitle} numberOfLines={2}>{entry.title}</Text>
            
            {/* 加入行程按鈕 - 實心橙色圓形 + 白色加號 */}
            {onAddToItinerary && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToItinerary({
                    title: entry.title,
                    url: entry.payload?.website || entry.payload?.resolvedFinalUrl,
                    imageUrl: entry.payload?.imageUrl || entry.coverImage?.url,
                    description: entry.summary || entry.payload?.description,
                    payload: entry.payload,
                  });
                }}
                aria-label={_t('snapshot.addToItinerary')}
                title={_t('snapshot.addToItineraryTitle')}
                style={{ ...(styles.addToItineraryButton as any), marginLeft: 8 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="#FFFFFF" />
                </svg>
              </button>
            )}
            </View>
          </View>
          
          {/* 外部分享卡片內容 */}
          <CardRenderer 
            it={cardItem} 
            isExpanded={isExpanded}
            onToggleExpand={() => setIsExpanded(!isExpanded)}
            isTravelPost={true}
          />
          
          {/* 外部鏈接按鈕 */}
          {externalUrl && (
            <a
              href={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                width: '100%',
                padding: '14px 16px',
                marginTop: 0,
                backgroundColor: '#FF6B35',
                color: '#FFFFFF',
                borderRadius: 0,
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                border: 'none',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLAnchorElement).style.backgroundColor = '#E55A2B';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLAnchorElement).style.backgroundColor = '#FF6B35';
              }}
            >
              {favicon && (
                <img 
                  src={favicon} 
                  alt="" 
                  style={{ width: 20, height: 20, borderRadius: 4 }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
              <span>{getButtonText()}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z" fill="currentColor"/>
              </svg>
            </a>
          )}
          
          {/* 筆記 */}
          {hasNotes && (
            <View style={{ padding: 16 }}>
              {entry.notes!.map((n, i) => renderNote(n, i))}
            </View>
          )}
        </div>
      );
    }
    
    // 對於 transport 使用原來的 View
    return (
      <View key={entry.id} style={styles.entryContainer}>
        {/* 使用與其他卡片相同的 header 布局 */}
        <View style={styles.entryHeader}>
          <View style={styles.entryNumberBadge}>
            <Text style={styles.entryNumberText}>{entryIndex + 1}</Text>
          </View>
        </View>
        
        {/* 交通卡片內容 */}
        <View style={styles.transportCardContent}>
          <CardRenderer 
            it={cardItem} 
            isExpanded={isExpanded}
            onToggleExpand={() => setIsExpanded(!isExpanded)}
          />
        </View>
        
        {/* 外部連結按鈕 - 交通卡片 */}
        {(() => {
          const externalUrl = entry.card?.base_payload?.url || entry.payload?.url || entry.meta?.url;
          if (externalUrl && typeof externalUrl === 'string' && externalUrl.startsWith('http')) {
            let siteName = '訪問連結';
            try {
              const urlObj = new URL(externalUrl);
              const hostname = urlObj.hostname.replace('www.', '');
              if (hostname.includes('trip.com')) siteName = '在 Trip.com 查看';
              else if (hostname.includes('booking.com')) siteName = '在 Booking.com 查看';
              else if (hostname.includes('agoda.com')) siteName = '在 Agoda 查看';
              else if (hostname.includes('expedia.com')) siteName = '在 Expedia 查看';
              else if (hostname.includes('klook.com')) siteName = '在 Klook 查看';
              else if (hostname.includes('kkday.com')) siteName = '在 KKday 查看';
              else siteName = `訪問 ${hostname}`;
            } catch (e) {}
            return (
              <View style={{ marginTop: 16, marginBottom: -16, marginLeft: -16, marginRight: -16 }}>
                <a
                  href={externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    padding: '14px 24px',
                    backgroundColor: '#FF6B35',
                    color: '#FFFFFF',
                    borderRadius: 0,
                    borderBottomLeftRadius: 11,
                    borderBottomRightRadius: 11,
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: 16,
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#E55A2B';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#FF6B35';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 3h7v7h-2V6.41L10.41 15 9 13.59 17.59 5H14V3zM5 5h6v2H7v10h10v-4h2v6H5V5z" fill="currentColor"/>
                  </svg>
                  <span>{siteName}</span>
                </a>
              </View>
            );
          }
          return null;
        })()}

        {/* 筆記 */}
        {hasNotes && (
          <View style={styles.notesSection}>
            {entry.notes!.map((n, i) => renderNote(n, i))}
          </View>
        )}
      </View>
    );
  }

  // 默認路徑：使用統一的卡片佈局（圖片在上、描述在下、有展開/收起）
  // 構建 CardRenderer 期望的格式
  const cardItem = {
    id: entry.id,
    title: entry.title,
    type: entry.meta?.itemType || 'activity',
    time: entry.meta?.start_time,
    start_time: entry.meta?.start_time,
    end_time: entry.meta?.end_time,
    meta: entry.meta,
    payload: entry.payload,
    custom_payload: entry.payload,
    card: entry.card,
    card_id: entry.meta?.card_id,
  };
  
  // 獲取外部鏈接信息
  const defaultExternalUrl = entry.card?.base_payload?.url || entry.payload?.url || entry.meta?.url;
  const defaultSiteName = entry.card?.base_payload?.siteName || entry.payload?.siteName || '';
  const defaultPartner = entry.card?.base_payload?.partner || entry.payload?.partner || '';
  const defaultFavicon = entry.card?.base_payload?.favicon || entry.payload?.favicon;
  
  const getDefaultButtonText = () => {
    const p = (defaultPartner || '').toLowerCase();
    const s = (defaultSiteName || '').toLowerCase();
    if (p.includes('trip') || s.includes('trip')) return '在 Trip.com 查看';
    if (p.includes('klook') || s.includes('klook')) return '在 Klook 查看';
    if (p.includes('kkday') || s.includes('kkday')) return '在 KKday 查看';
    if (p.includes('booking') || s.includes('booking')) return '在 Booking.com 查看';
    if (p.includes('agoda') || s.includes('agoda')) return '在 Agoda 查看';
    if (defaultSiteName) return `在 ${defaultSiteName} 查看`;
    // 從 URL 中提取
    if (defaultExternalUrl) {
      try {
        const urlObj = new URL(defaultExternalUrl);
        const hostname = urlObj.hostname.replace('www.', '');
        if (hostname.includes('trip.com')) return '在 Trip.com 查看';
        if (hostname.includes('booking.com')) return '在 Booking.com 查看';
        if (hostname.includes('agoda.com')) return '在 Agoda 查看';
        if (hostname.includes('klook.com')) return '在 Klook 查看';
        if (hostname.includes('kkday.com')) return '在 KKday 查看';
        return `訪問 ${hostname}`;
      } catch (e) {}
    }
    return '查看詳情';
  };

  return (
    <div key={entry.id} style={{
      marginBottom: 24,
      backgroundColor: '#fff',
      borderRadius: 12,
      border: '1px solid #E5E7EB',
      position: 'relative',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <View style={{ padding: 16, paddingBottom: 0 }}>
        <View style={styles.entryHeader}>
          <View style={styles.entryNumberBadge}>
            <Text style={styles.entryNumberText}>{entryIndex + 1}</Text>
          </View>
          <Text style={styles.entryTitle}>{entry.title}</Text>

          {/* 如果條目關聯了卡片，顯示一個圓形 icon，點擊前往卡片詳情 */}
          {((entry.meta && (entry.meta.card_id || entry.meta.cardId)) || (entry as any).card?.id) && (
            <button
              onClick={() => {
                const cid = entry.meta?.card_id || (entry as any).card?.id || entry.meta?.cardId;
                if (!cid) return;
                if (typeof window !== 'undefined') {
                  window.location.href = `/card/${encodeURIComponent(String(cid))}`;
                }
              }}
              aria-label="打開卡片詳情"
              title="在新視窗打開卡片"
              style={{ ...(styles.cardIconButton as any), marginLeft: 12 }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M14 3h7v7h-2V6.41L10.41 15 9 13.59 17.59 5H14V3zM5 5h6v2H7v10h10v-4h2v6H5V5z" fill="#FF6B35" stroke="#FF6B35" strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </View>
      </View>
      
      {/* 卡片內容 - 使用 CardRenderer */}
      <CardRenderer 
        it={cardItem} 
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded(!isExpanded)}
        isTravelPost={true}
      />
      
      {/* 外部鏈接按鈕 */}
      {defaultExternalUrl && typeof defaultExternalUrl === 'string' && defaultExternalUrl.startsWith('http') && (
        <a
          href={defaultExternalUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            width: '100%',
            padding: '14px 16px',
            marginTop: 0,
            backgroundColor: '#FF6B35',
            color: '#FFFFFF',
            borderRadius: 0,
            textDecoration: 'none',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLAnchorElement).style.backgroundColor = '#E55A2B';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLAnchorElement).style.backgroundColor = '#FF6B35';
          }}
        >
          {defaultFavicon && (
            <img 
              src={defaultFavicon} 
              alt="" 
              style={{ width: 20, height: 20, borderRadius: 4 }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          <span>{getDefaultButtonText()}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z" fill="currentColor"/>
          </svg>
        </a>
      )}
      
      {/* 筆記 */}
      {hasNotes && (
        <View style={{ padding: 16 }}>
          {entry.notes!.map((n, i) => renderNote(n, i))}
        </View>
      )}
    </div>
  );
};

/**
 * SnapshotContentRenderer - 旅行指南快照內容渲染器
 * 用於渲染從 itinerary snapshot 生成的旅行指南內容
 */
const SnapshotContentRenderer: React.FC<SnapshotContentRendererProps> = ({ contentJson, authorId, t, locale = 'zh-CN', onAddToItinerary, onOpenLightbox, onTagPress }) => {
  const _t = t || ((key: string, options?: any) => {
    // Fallback for when t is not provided
    if (key === 'snapshot.noContent') return '暫無內容';
    if (key === 'snapshot.imageLoadFailed') return '圖片載入失敗';
    if (key === 'snapshot.day') return `第 ${options?.number} 天`;
    if (key === 'snapshot.openCardDetails') return '打開卡片詳情';
    if (key === 'snapshot.openCardInNewWindow') return '在新視窗打開卡片';
    if (key === 'snapshot.prevImage') return '上一張';
    if (key === 'snapshot.nextImage') return '下一張';
    if (key === 'snapshot.openExternalLink') return '打開連結';
    if (key === 'snapshot.openExternalLinkTitle') return '在新視窗打開連結';
    if (key === 'snapshot.addToItinerary') return '加入行程';
    if (key === 'snapshot.addToItineraryTitle') return '將此項目加入我的行程';
    return key;
  });

  const content: SnapshotContent = React.useMemo(() => {
    try {
      if (typeof contentJson === 'string') {
        return JSON.parse(contentJson);
      }
      return contentJson;
    } catch (err) {
      console.error('Failed to parse snapshot content:', err);
      return { days: [] };
    }
  }, [contentJson]);

  if (!content.days || content.days.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.emptyText}>{_t('snapshot.noContent')}</Text>
      </View>
    );
  }

  const [lightboxState, setLightboxState] = React.useState<{
    images: SnapshotImage[];
    currentIndex: number;
  } | null>(null);

  const openLightbox = (images: SnapshotImage[], startIndex: number) => {
    if (onOpenLightbox) {
      onOpenLightbox(images, startIndex);
    } else {
      setLightboxState({ images, currentIndex: startIndex });
    }
  };

  const closeLightbox = () => {
    setLightboxState(null);
  };

  const nextImage = () => {
    if (!lightboxState) return;
    setLightboxState({
      ...lightboxState,
      currentIndex: (lightboxState.currentIndex + 1) % lightboxState.images.length
    });
  };

  const prevImage = () => {
    if (!lightboxState) return;
    setLightboxState({
      ...lightboxState,
      currentIndex: (lightboxState.currentIndex - 1 + lightboxState.images.length) % lightboxState.images.length
    });
  };

  const renderDay = (day: SnapshotDay, dayIndex: number) => {
    const dayNumber = day.dayNumber ?? dayIndex + 1;
    const dateStr = day.date
      ? new Date(day.date).toLocaleDateString(locale, {
          month: 'long',
          day: 'numeric',
        })
      : '';

    return (
      <View key={day.id} style={styles.dayContainer}>
        {/* Day 標題 */}
        <View style={styles.dayHeader}>
          <Text style={styles.dayTitle}>{_t('snapshot.day', { number: dayNumber })}</Text>
          {dateStr && <Text style={styles.dayDate}>{dateStr}</Text>}
        </View>

        {/* Day 內的所有條目 */}
        {day.entries.map((entry, entryIdx) => (
          <SnapshotEntryItem
            key={entry.id}
            entry={entry}
            entryIndex={entryIdx}
            authorId={authorId}
            t={_t}
            openLightbox={openLightbox}
            onAddToItinerary={onAddToItinerary}
            onTagPress={onTagPress}
          />
        ))}
      </View>
    );
  };

  return (
    <>
      <View style={styles.container}>
        {content.days.map((day, dayIdx) => renderDay(day, dayIdx))}
      </View>
      
      {/* 使用 Portal 渲染燈箱到 body */}
      {lightboxState && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 2147483647, // Max z-index to cover everything including bottom nav
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none', // Prevent background scrolling
            overscrollBehavior: 'contain',
          }}
          onClick={closeLightbox}
        >
          <img
            src={lightboxState.images[lightboxState.currentIndex].url}
            alt={lightboxState.images[lightboxState.currentIndex].alt || ''}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* 關閉按鈕 */}
          <button
            onClick={closeLightbox}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 'none',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#000',
              fontSize: 20,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
            }}
          >
            <MdClose size={24} />
          </button>
          
          {lightboxState.images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                style={{
                  position: 'absolute',
                  left: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#000',
                  fontSize: 24,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                }}
              >
                <MdChevronLeft size={30} />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                style={{
                  position: 'absolute',
                  right: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#000',
                  fontSize: 24,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                }}
              >
                <MdChevronRight size={30} />
              </button>
              
              <div style={{
                position: 'absolute',
                bottom: 30,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: 16,
                fontSize: 14,
                fontWeight: 500,
              }}>
                {lightboxState.currentIndex + 1} / {lightboxState.images.length}
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  emptyText: {
    fontSize: 14,
    color: '#ccc',
    textAlign: 'center',
    paddingVertical: 32,
  },
  dayContainer: {
    marginBottom: 32,
  },
  dayHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: '#FF6B35',
  },
  dayTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1a1a1a',
  },
  dayDate: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  entryContainer: {
    marginBottom: 24,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    position: 'relative',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  entryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  entryNumberBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#FF6B35',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  entryNumberText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  cardIconButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    border: 'none',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: 'none',
    padding: 0,
  } as any,
  addToItineraryButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    border: 'none',
    backgroundColor: '#FF6B35',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: 0,
    transition: 'all 0.2s ease',
  } as any,
  /* cardIconAbsolute kept for backward compatibility but no longer used */
  cardIconAbsolute: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 5,
  } as any,
  entryTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    color: '#1a1a1a',
    lineHeight: 24,
  },
  entrySummary: {
    fontSize: 15,
    lineHeight: 22,
    color: '#666',
    marginBottom: 12,
  },
  singleImageContainer: {
    marginVertical: 12,
    marginHorizontal: -16,
    cursor: 'pointer',
    overflow: 'hidden',
  } as any,
  singleImage: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#f5f5f5',
  },
  galleryWrapper: {
    position: 'relative',
    marginVertical: 12,
  } as any,
  galleryScroll: {
    marginHorizontal: -16,
    paddingHorizontal: 16,
  },
  galleryContent: {
    paddingRight: 16,
  },
  galleryImageWrapper: {
    marginRight: 12,
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
  } as any,
  galleryImage: {
    width: 280,
    aspectRatio: 16 / 9,
    borderRadius: 12,
    backgroundColor: '#f5f5f5',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  } as any,
  galleryNavButton: {
    position: 'absolute',
    top: '50%',
    transform: [{ translateY: -24 }],
    zIndex: 10,
  } as any,
  galleryNavLeft: {
    left: 8,
  } as any,
  galleryNavRight: {
    right: 8,
  } as any,
  notesSection: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  notesSectionNoBorder: {
    marginTop: 16,
    paddingTop: 12,
  },
  noteContainer: {
    marginBottom: 12,
    padding: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  noteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  noteAuthorAvatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
    marginRight: 8,
    backgroundColor: '#E5E7EB',
  },
  noteAuthorName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
  },
  noteText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#374151',
    marginBottom: 8,
  },
  noteImagesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  noteImage: {
    width: 72,
    height: 72,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
  },
  transportCardContent: {
    marginLeft: -16,
    marginRight: -16,
    marginBottom: -16,
  },
});

export default React.memo(SnapshotContentRenderer, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders when only functions or irrelevant props change
  // We mainly care about contentJson and authorId
  // If contentJson is the same object reference (which we ensured in parent), we skip re-render
  // If authorId is the same, we skip re-render
  
  // Note: We ignore 't' and 'onAddToItinerary' changes assuming they are stable or their changes shouldn't trigger a full re-render that resets state
  // However, if language changes, 't' might change, but usually that triggers a full app re-render anyway.
  
  if (prevProps.contentJson !== nextProps.contentJson) return false;
  if (prevProps.authorId !== nextProps.authorId) return false;
  if (prevProps.locale !== nextProps.locale) return false;
  
  return true;
});
